/**
 * 📍 SiteFields.js - Recording site details (all optional)
 *
 * Used by StationUpload.js (per upload, pre-filled from the station's defaults)
 * and StationSettings.js (the station's defaults).
 *
 * Stored in Firestore (always in METRES, whatever unit the operator typed):
 *   stations/{id}.siteDefaults        = { waterDepthM, hydrophoneDepthM, bottomType, seaState,
 *                                         hydrophoneSensitivityDb, recorderGainDb, fullScaleVolts,
 *                                         endToEndDbRe1uPaAtFullScale, hydrophoneSpacingM, towedArray,
 *                                         siteName, latitude, longitude }
 *   stations/{id}/files/{id}.site     = the same fields for one upload
 *
 * The dolphin engine reads them:
 *   waterDepthM + hydrophoneDepthM  -> distance and depth of each dolphin (from surface/seabed echoes)
 *   bottomType, seaState            -> how much to trust those positions
 *   hydrophoneSensitivityDb + fullScaleVolts (+ recorderGainDb) -> real sound levels (clicks, noise levels)
 *   siteName, latitude, longitude (8 Oct 2026) -> where THIS recording was made (a station can record in several
 *                                          places); noise levels are grouped by station + site, seasons by latitude
 *   endToEndDbRe1uPaAtFullScale (8 Oct 2026) -> the same in one number, from a calibrator (used instead of the three)
 *   hydrophoneSpacingM (2-channel recordings) -> direction of each click; towedArray -> ship noise marked
 */

import React, { useState } from 'react';

export const BOTTOM_TYPES = [
  { id: '', name: 'Not sure' },
  { id: 'sand', name: 'Sand' },
  { id: 'rock', name: 'Rock' },
  { id: 'mud', name: 'Mud / silt' },
  { id: 'seagrass', name: 'Seagrass' },
  { id: 'coral', name: 'Coral' },
];

export const SEA_STATES = [
  { id: '', name: 'Not sure' },
  { id: '0', name: '0 - Calm (glassy)' },
  { id: '1', name: '1 - Ripples' },
  { id: '2', name: '2 - Small wavelets' },
  { id: '3', name: '3 - Large wavelets' },
  { id: '4', name: '4 - Small waves' },
  { id: '5', name: '5 - Moderate waves' },
  { id: '6', name: '6+ - Rough' },
];

export const EMPTY_SITE = {
  waterDepthM: '', hydrophoneDepthM: '', bottomType: '', seaState: '',
  hydrophoneSensitivityDb: '', recorderGainDb: '', fullScaleVolts: '', endToEndDbRe1uPaAtFullScale: '',
  hydrophoneSpacingM: '', towedArray: false, siteName: '', latitude: '', longitude: '',
};

const FT = 0.3048;

/** Clean a site object for Firestore: numbers as numbers, empty values left out. */
export const cleanSite = (site) => {
  const out = {};
  Object.entries(site || {}).forEach(([k, v]) => {
    if (v === '' || v === null || v === undefined) return;
    if (k === 'towedArray') { if (v === true) out[k] = true; return; }
    if (['bottomType', 'siteName'].includes(k)) out[k] = String(v).trim().slice(0, 80);
    else {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) out[k] = n;
    }
  });
  return out;
};

/** Check the numbers make sense; returns a message or ''. */
export const checkSite = (site) => {
  const s = cleanSite(site);
  if (s.hydrophoneDepthM !== undefined && s.waterDepthM !== undefined && s.hydrophoneDepthM >= s.waterDepthM) {
    return 'Hydrophone depth must be less than the water depth.';
  }
  if (s.waterDepthM !== undefined && (s.waterDepthM <= 0 || s.waterDepthM > 11000)) return 'Water depth looks wrong.';
  if (s.latitude !== undefined && (s.latitude < -90 || s.latitude > 90)) return 'Latitude must be between -90 and 90.';
  if (s.longitude !== undefined && (s.longitude < -180 || s.longitude > 180)) return 'Longitude must be between -180 and 180.';
  if (s.hydrophoneSensitivityDb !== undefined && (s.hydrophoneSensitivityDb > -100 || s.hydrophoneSensitivityDb < -250)) {
    return 'Hydrophone sensitivity is usually between -150 and -220 dB re 1 V/µPa (a negative number).';
  }
  if (s.endToEndDbRe1uPaAtFullScale !== undefined && (s.endToEndDbRe1uPaAtFullScale < 80 || s.endToEndDbRe1uPaAtFullScale > 260)) {
    return 'End-to-end calibration is usually between 100 and 220 dB re 1 µPa at full scale.';
  }
  return '';
};

const box = { background: 'rgba(0,180,216,0.06)', border: '1px solid rgba(0,180,216,0.25)',
  borderRadius: 10, padding: 14, marginTop: 14 };
const help = { color: '#667', fontSize: 12, fontStyle: 'italic', lineHeight: 1.45, margin: '4px 0 10px' };
const row = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 };

/**
 * props: site (object in metres), onChange(newSite), title?, showSaveDefault?, saveDefault?, onSaveDefault?
 */
const SiteFields = ({ site, onChange, title = '📍 Recording site (optional)',
  showSaveDefault = false, saveDefault = true, onSaveDefault }) => {
  const [unit, setUnit] = useState('m');
  const [showCal, setShowCal] = useState(
    !!(site?.hydrophoneSensitivityDb || site?.recorderGainDb || site?.fullScaleVolts || site?.endToEndDbRe1uPaAtFullScale));
  const [showPair, setShowPair] = useState(!!(site?.hydrophoneSpacingM || site?.towedArray));

  // what the operator is typing (so "6." in feet is not reformatted mid-typing)
  const [drafts, setDrafts] = useState({});
  const shown = (k) => {
    if (drafts[k] !== undefined) return drafts[k];
    const v = site?.[k];
    if (v === '' || v === undefined || v === null) return '';
    return unit === 'm' ? v : +(parseFloat(v) / FT).toFixed(2);
  };
  const setDepth = (k, raw) => {
    setDrafts({ ...drafts, [k]: raw });
    if (raw === '') return onChange({ ...site, [k]: '' });
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    onChange({ ...site, [k]: unit === 'm' ? n : +(n * FT).toFixed(3) });
  };
  const changeUnit = (u) => { setDrafts({}); setUnit(u); };
  const set = (k, v) => onChange({ ...site, [k]: v });
  const warning = checkSite(site);

  return (
    <div style={box}>
      <h4 style={{ margin: 0 }}>{title}</h4>
      <p style={help}>
        With the <b>water depth</b> and <b>hydrophone depth</b> we can estimate how far away and how deep each
        dolphin was, from the echoes of its clicks off the surface and the seabed. This works best in water
        3–25 m deep, with a calm sea and a sandy or rocky bottom. Leave anything you don't know blank.
      </p>

      <div style={row}>
        <div className="form-group">
          <label>Site name</label>
          <input type="text" maxLength={80} value={site?.siteName ?? ''} onChange={(e) => set('siteName', e.target.value)} placeholder="e.g. North reef" />
        </div>
        <div className="form-group">
          <label>Latitude (°, south negative)</label>
          <input type="number" step="0.0001" min="-90" max="90" value={site?.latitude ?? ''} onChange={(e) => set('latitude', e.target.value)} placeholder="e.g. 21.29" />
        </div>
        <div className="form-group">
          <label>Longitude (°, west negative)</label>
          <input type="number" step="0.0001" min="-180" max="180" value={site?.longitude ?? ''} onChange={(e) => set('longitude', e.target.value)} placeholder="e.g. -157.85" />
        </div>
      </div>
      <p style={help}>If you record in more than one place, give each place a name: noise levels and results are kept per site.</p>
      <div style={row}>
        <div className="form-group">
          <label>Water depth ({unit})</label>
          <input type="number" min="0" step="0.1" value={shown('waterDepthM')}
            onChange={(e) => setDepth('waterDepthM', e.target.value)} placeholder="e.g. 8" />
        </div>
        <div className="form-group">
          <label>Hydrophone depth ({unit})</label>
          <input type="number" min="0" step="0.1" value={shown('hydrophoneDepthM')}
            onChange={(e) => setDepth('hydrophoneDepthM', e.target.value)} placeholder="e.g. 3" />
        </div>
        <div className="form-group">
          <label>Units</label>
          <select value={unit} onChange={(e) => changeUnit(e.target.value)}>
            <option value="m">metres</option>
            <option value="ft">feet</option>
          </select>
        </div>
        <div className="form-group">
          <label>Bottom type</label>
          <select value={site?.bottomType || ''} onChange={(e) => set('bottomType', e.target.value)}>
            {BOTTOM_TYPES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Sea state</label>
          <select value={site?.seaState === 0 ? '0' : (site?.seaState ?? '')}
            onChange={(e) => set('seaState', e.target.value)}>
            {SEA_STATES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      <button type="button" onClick={() => setShowCal(!showCal)}
        style={{ background: 'none', border: 'none', color: '#0C71C3', cursor: 'pointer',
          padding: '8px 0', fontWeight: 600 }}>
        {showCal ? '▾' : '▸'} Calibration (only if you know it)
      </button>

      {showCal && (
        <>
          <p style={help}>
            These turn "how loud on the recording" into real sound levels (dB re 1 µPa). Combined with the
            distance, they tell us how loud the dolphin itself was. They are not needed for positions.
            You find them on the hydrophone's and recorder's data sheets.
          </p>
          <div style={row}>
            <div className="form-group">
              <label>Hydrophone sensitivity (dB re 1 V/µPa)</label>
              <input type="number" step="0.1" value={site?.hydrophoneSensitivityDb ?? ''}
                onChange={(e) => set('hydrophoneSensitivityDb', e.target.value)} placeholder="e.g. -180" />
            </div>
            <div className="form-group">
              <label>Recorder gain (dB)</label>
              <input type="number" step="0.1" value={site?.recorderGainDb ?? ''}
                onChange={(e) => set('recorderGainDb', e.target.value)} placeholder="e.g. 20" />
            </div>
            <div className="form-group">
              <label>Recorder full scale (volts peak)</label>
              <input type="number" step="0.01" min="0" value={site?.fullScaleVolts ?? ''}
                onChange={(e) => set('fullScaleVolts', e.target.value)} placeholder="e.g. 1.0" />
            </div>
          </div>
          <p style={help}>Sound levels are calculated only when sensitivity and full scale are both filled in.</p>
          <p style={help}>
            <b>Or, if you calibrated the whole system with a calibrator</b> (a known tone played into the hydrophone):
            the sound level that would fill the recorder's full scale. Example: a 157 dB re 1 µPa calibrator tone reads
            −30 dB re full scale in the recording → 157 + 30 = 187. This is used instead of the three values above.
          </p>
          <div style={row}>
            <div className="form-group">
              <label>End-to-end calibration (dB re 1 µPa at full scale)</label>
              <input type="number" step="0.1" value={site?.endToEndDbRe1uPaAtFullScale ?? ''}
                onChange={(e) => set('endToEndDbRe1uPaAtFullScale', e.target.value)} placeholder="e.g. 187" />
            </div>
          </div>
          <p style={help}>Without any calibration the noise levels and click levels are relative (dB re full scale) and marked "uncalibrated".</p>
        </>
      )}

      <button type="button" onClick={() => setShowPair(!showPair)}
        style={{ background: 'none', border: 'none', color: '#0C71C3', cursor: 'pointer',
          padding: '8px 0', fontWeight: 600, display: 'block' }}>
        {showPair ? '▾' : '▸'} Two hydrophones (stereo recording or towed array)
      </button>

      {showPair && (
        <>
          <p style={help}>
            If your recording has two channels from two hydrophones, we use the tiny time difference between
            them to tell dolphins apart by direction and to remove recorder noise. Give the distance between the
            two hydrophones to get the direction in degrees. Tick "towed array" if the hydrophones were towed
            behind a boat, so the boat's own noise is recognised.
          </p>
          <div style={row}>
            <div className="form-group">
              <label>Distance between the hydrophones ({unit})</label>
              <input type="number" min="0" step="0.01" value={shown('hydrophoneSpacingM')}
                onChange={(e) => setDepth('hydrophoneSpacingM', e.target.value)} placeholder="e.g. 3" />
            </div>
            <label className="checkbox-label" style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={!!site?.towedArray} onChange={(e) => set('towedArray', e.target.checked)} />
              <span>Towed array (pulled behind a boat)</span>
            </label>
          </div>
        </>
      )}

      {warning && <p style={{ color: '#E65100', fontSize: 13, margin: '6px 0 0' }}>⚠️ {warning}</p>}

      {showSaveDefault && (
        <label className="checkbox-label" style={{ display: 'flex', gap: 8, marginTop: 10, fontSize: 13 }}>
          <input type="checkbox" checked={saveDefault} onChange={(e) => onSaveDefault?.(e.target.checked)} />
          <span>Remember these for my next uploads</span>
        </label>
      )}
    </div>
  );
};

export default SiteFields;
